import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../services/supabaseClient';
import {
  getUserSubscription,
  getPlans,
  countUserProjects,
  formatPrice
} from '../services/subscriptionService';
import analytics from '../services/analyticsService';
import { showToast } from '../components/common/Toast';
import type { Plan, PlanLimits, SubscriptionWithPlan } from '../types';
import type { SubscriptionContextValue } from '../types';

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user, profile } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionWithPlan | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [usage, setUsage] = useState<{ projects: number }>({ projects: 0 });
  const [loading, setLoading] = useState(true);
  const [adminStatus, setAdminStatus] = useState<{ is_admin: boolean; is_blocked: boolean }>({ is_admin: false, is_blocked: false });

  // Buscar is_admin diretamente do banco (fallback se profile não tiver)
  useEffect(() => {
    async function loadAdminStatus() {
      if (!user?.id) {
        setAdminStatus({ is_admin: false, is_blocked: false });
        return;
      }

      // Se o profile do AuthContext já tem is_admin, usar ele
      if (profile && typeof profile.is_admin !== 'undefined') {
        setAdminStatus({
          is_admin: profile.is_admin === true,
          is_blocked: profile.is_blocked === true
        });
        return;
      }

      // Senão, buscar direto do banco
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_admin, is_blocked')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          setAdminStatus({
            is_admin: data.is_admin === true,
            is_blocked: data.is_blocked === true
          });
        }
      } catch (err) {
        console.error('Erro ao buscar status admin:', err);
      }
    }

    loadAdminStatus();
  }, [user?.id, profile]);

  // Carregar planos disponíveis
  useEffect(() => {
    async function loadPlans() {
      try {
        const plansData = await getPlans();
        setPlans(plansData);
      } catch (error) {
        console.error('Erro ao carregar planos:', error);
      }
    }
    loadPlans();
  }, []);

  // Carregar assinatura e uso quando user mudar
  useEffect(() => {
    if (!user?.id) {
      setSubscription(null);
      setUsage({ projects: 0 });
      setLoading(false);
      return;
    }

    async function loadSubscription() {
      setLoading(true);
      try {
        const [sub, projectCount] = await Promise.all([
          getUserSubscription(user!.id),
          countUserProjects(user!.id)
        ]);
        setSubscription(sub);
        setUsage({ projects: projectCount });
      } catch (error) {
        console.error('Erro ao carregar assinatura:', error);
      } finally {
        setLoading(false);
      }
    }

    loadSubscription();
  }, [user?.id]);

  // Plano atual — se assinatura cancelada/expirada, fallback para Free
  const freePlan = plans.find(p => p.name === 'free') || null;

  // Verificar se trial expirou (trials internos sem Stripe)
  const isTrialExpired = subscription?.status === 'trialing'
    && subscription?.trial_end
    && new Date(subscription.trial_end).getTime() <= Date.now();

  const isSubscriptionActive = (subscription?.status === 'active' || subscription?.status === 'trialing') && !isTrialExpired;
  const plan = (subscription?.plan && isSubscriptionActive)
    ? subscription.plan
    : freePlan;
  const planName = plan?.name || 'free';
  // Valor de emergência, usado só quando o plano não carrega (falha de rede).
  // Tem que espelhar os limites do Free no banco (20260809100000), senão o
  // usuário vê um limite que já não existe.
  const limits: PlanLimits = plan?.limits || { projects: 3, members_per_project: 3 };
  const features: string[] = plan?.features || [];

  // Enviar user_id + propriedades de COORTE para o GA4 (segmenta todas as
  // métricas por origem, plano, idade de conta e status).
  useEffect(() => {
    if (!user?.id || loading) return;
    const createdMs = profile?.created_at ? new Date(profile.created_at).getTime() : null;
    analytics.setUser(user.id, {
      plan_name: planName,
      subscription_status: subscription?.status || 'free',
      is_trial: subscription?.status === 'trialing' && !isTrialExpired,
      days_since_signup: createdMs !== null ? Math.floor((Date.now() - createdMs) / 86400000) : null,
      signup_cohort_month: profile?.created_at ? profile.created_at.slice(0, 7) : null,
      role: profile?.role || null,
      utm_source: profile?.utm_source || 'direct',
      utm_medium: profile?.utm_medium || 'none',
      num_projects: usage.projects,
    });
  }, [user?.id, planName, loading, profile, subscription?.status, isTrialExpired, usage.projects]);

  // Verificar se pode criar projeto
  const canCreateProject = useCallback((): boolean => {
    const limit = limits.projects;
    if (limit === -1) return true;
    return usage.projects < limit;
  }, [limits.projects, usage.projects]);

  // Verificar se tem feature (fonte de verdade: banco de dados)
  const hasFeature = useCallback((feature: string): boolean => {
    return features.includes(feature);
  }, [features]);

  // Verificar limite de membros por projeto
  const canAddMemberToProject = useCallback((currentMemberCount: number): boolean => {
    const limit = limits.members_per_project;
    if (limit === -1) return true;
    return currentMemberCount < limit;
  }, [limits.members_per_project]);

  // Verificar se está no limite
  const isAtProjectLimit = useCallback((): boolean => {
    const limit = limits.projects;
    if (limit === -1) return false;
    return usage.projects >= limit;
  }, [limits.projects, usage.projects]);

  // Atualizar uso
  const refreshUsage = useCallback(async (): Promise<void> => {
    if (!user?.id) return;
    try {
      const projectCount = await countUserProjects(user.id);
      setUsage({ projects: projectCount });
    } catch (error) {
      console.error('Erro ao atualizar uso:', error);
    }
  }, [user?.id]);

  // Recarregar assinatura (após upgrade/downgrade)
  const refreshSubscription = useCallback(async (): Promise<void> => {
    if (!user?.id) return;
    try {
      const sub = await getUserSubscription(user.id);
      setSubscription(sub);
    } catch (error) {
      console.error('Erro ao recarregar assinatura:', error);
    }
  }, [user?.id]);

  // Trial helpers
  const isTrialing = subscription?.status === 'trialing' && !isTrialExpired;
  const trialDaysRemaining = (() => {
    if (subscription?.status !== 'trialing' || !subscription?.trial_end) return null;
    const diffMs = new Date(subscription.trial_end).getTime() - Date.now();
    if (diffMs <= 0) return 0;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  })();

  // Timer: detectar expiração do trial em tempo real (sem reload)
  const trialExpiredNotified = useRef(false);
  useEffect(() => {
    if (subscription?.status !== 'trialing' || !subscription?.trial_end) {
      trialExpiredNotified.current = false;
      return;
    }

    const checkExpiration = () => {
      const remaining = new Date(subscription.trial_end!).getTime() - Date.now();
      if (remaining <= 0 && !trialExpiredNotified.current) {
        trialExpiredNotified.current = true;
        const trialDays = profile?.created_at
          ? Math.round((new Date(subscription.trial_end!).getTime() - new Date(profile.created_at).getTime()) / 86400000)
          : 14;
        analytics.trackTrialExpired(trialDays);
        showToast('Seu período de teste expirou. Você agora está no plano Free.', 'info');
        refreshSubscription();
      }
    };

    // Checar imediatamente e depois a cada 60s
    checkExpiration();
    const interval = setInterval(checkExpiration, 60_000);
    return () => clearInterval(interval);
  }, [subscription?.status, subscription?.trial_end, refreshSubscription, profile]);

  // Verificar se é admin do sistema (lido do banco)
  const isSystemAdmin = adminStatus.is_admin === true;
  const isBlocked = adminStatus.is_blocked === true;

  const value: SubscriptionContextValue = {
    // Estado
    subscription,
    plan,
    planName,
    plans,
    limits,
    features,
    usage,
    loading,

    // Verificações
    canCreateProject,
    hasFeature,
    canAddMemberToProject,
    isAtProjectLimit,

    // Admin e Bloqueio (lidos do banco)
    isSystemAdmin,
    isBlocked,

    // Ações
    refreshUsage,
    refreshSubscription,

    // Helpers
    formatPrice,

    // Status
    isPro: planName === 'professional' || planName === 'business',
    isBusiness: planName === 'business',
    isStarter: planName === 'starter',
    isFree: planName === 'free',
    isActive: isSubscriptionActive,
    isTrialing,
    isTrialExpired: !!isTrialExpired,
    trialDaysRemaining,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextValue {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription deve ser usado dentro de SubscriptionProvider');
  }
  return context;
}
